<template>
  <div class="list-container">
    <h1 class="page-title">業務区分マスタ情報一覧</h1>
    <div class="mb-2">
      <router-link v-if="userRole == 0 || userRole == 999" to="/create-business-classification" class="btn btn-sm btn-primary">業務区分マスタ情報作成</router-link> <!-- 業務区分マスタ情報作成ページへのリンク -->

      <button v-if="userRole == 0 || userRole == 999" type="button" class="btn btn-sm btn-primary mx-2" @click="downloadCsv">
        CSVエクスポート
      </button>

      <!-- Button trigger modal -->
      <button v-if="userRole == 999" type="button" class="btn btn-sm btn-primary mx-2" data-bs-toggle="modal" data-bs-target="#exampleModal">
        CSVインポート
      </button>
    </div>

    <!-- Modal -->
    <div class="modal fade" id="exampleModal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" aria-labelledby="staticBackdropLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h1 class="modal-title fs-5" id="exampleModalLabel">CSVインポート</h1>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" @click="clearErrorMessages"></button>
          </div>
          <div class="modal-body">
            <div v-if="errorMessages" >
              <div class="error-massage-item" v-for="errorMessage in errorMessages" :key="errorMessage.id">
                <p class="alert alert-danger mt-2">{{ errorMessage.row_num }}行目：{{errorMessage.messages[0]}}</p>
              </div>
            </div>

            <div class="my-3">
              <p>業務区分情報CSVファイル選択</p>
              <input type="file" class="form-control" @change="handleFileChange">
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="clearErrorMessages">閉じる</button>
            <button type="button" class="btn btn-primary" @click="submitFile">インポート</button>
          </div>
        </div>
      </div>
    </div>

    <form class="search-container mb-3" style="background-color: #f8f9fa;" @submit.prevent="searchBusinessClassifications">
      <div class="row g-3 align-items-center">
        <DeletedStatusSelect v-model="searchDeletedStatus" id="businessClassificationDeletedStatus" />

        <div class="col-auto">
          <button type="submit" class="btn btn-sm btn-primary mx-1">検索</button>
          <button type="button" class="btn btn-sm btn-secondary mx-1" @click="searchResetBusinessClassifications">クリア</button>
        </div>
      </div>
    </form>

    <div class="table-wrapper">
      <table class="master-table table table-bordered">
        <thead class="table-dark">
          <tr>
            <th>ID</th>
            <th>並び順</th>
            <th>業務区分名</th>
            <th class="btn-cell"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="business_classification in business_classifications"
            :key="business_classification.id"
            :class="{ 'no-active-business-classification': business_classification.del_flg == 1 }"
          >
            <td>{{ business_classification.id }}</td>
            <td>{{ business_classification.sort_no }}</td>
            <td>{{ business_classification.business_classification_nm }}</td>
            <td class="btn-cell text-nowrap">
              <router-link v-if="userRole == 0 || userRole == 999" :to="{ name: 'BusinessClassificationEdit', params: { id: business_classification.id }}"> <!-- 建物情報編集ページへのリンク -->
                <button type="button" class="btn btn-sm btn-success me-2">編集</button>
              </router-link>

              <button v-if="business_classification.del_flg == 0 && (userRole == 0 || userRole == 999)" class="btn btn-sm btn-danger" data-bs-toggle="modal" data-bs-target="#deleteModal" @click="handleDeleteClick(business_classification.id)">削除</button>
              <button v-if="business_classification.del_flg == 1 && (userRole == 0 || userRole == 999)" class="btn btn-sm btn-warning" data-bs-toggle="modal" data-bs-target="#restoreModal" @click="handleRestoreClick(business_classification.id)">復元</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 削除確認モーダル -->
    <DeleteConfirmationModal
      :paramsId="paramsIdToDelete"
      @confirmDelete="handleConfirmDelete"
    />

    <!-- 復元確認モーダル -->
    <RestoreConfirmationModal
      :paramsId="paramsIdToRestore"
      @confirmRestore="handleConfirmRestore"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watchEffect } from 'vue';
import { useStore } from 'vuex';
import {
  getBusinessClassifications,
  deleteBusinessClassification,
  restoreBusinessClassification,
  exportBusinessClassificationsCsv,
  importBusinessClassificationsCsv
} from '@/api/axios';
import { Modal } from 'bootstrap';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal.vue';
import RestoreConfirmationModal from '@/components/shared/RestoreConfirmationModal.vue';
import DeletedStatusSelect from '@/components/shared/DeletedStatusSelect.vue';

export default {
  components: {
    DeleteConfirmationModal,
    RestoreConfirmationModal,
    DeletedStatusSelect
  },
  setup() {
    const title = ref('業務区分情報一覧');

    watchEffect(() => {
      document.title = title.value;
    });

    const store = useStore();
    const userRole = computed(() => store.state.userRole);
    const business_classifications = ref([]);
    const selectedFile = ref(null);
    const searchDeletedStatus = ref('active');

     // エラーメッセージ用のリアクティブプロパティ
    const errorMessages = ref({});

    // 削除モーダルへ渡すID
    const paramsIdToDelete = ref(null);
    const paramsIdToRestore = ref(null);

    const fetchBusinessClassifications = async () => {
      try {
        const response = await getBusinessClassifications({ deleted_status: searchDeletedStatus.value });
        business_classifications.value = response.data;
      } catch (error) {
        console.error('業務区分情報ーデータの取得に失敗しました:', error);
      }
    };

    const searchBusinessClassifications = async () => {
      await fetchBusinessClassifications();
    };

    const searchResetBusinessClassifications = async () => {
      searchDeletedStatus.value = 'active';
      await fetchBusinessClassifications();
    };

    const handleDeleteClick = (businessClassificationId) => {
      paramsIdToDelete.value = businessClassificationId;
    };

    const handleRestoreClick = (businessClassificationId) => {
      paramsIdToRestore.value = businessClassificationId;
    };
    
    const handleConfirmDelete = async (businessClassificationId) => {
      try {
        await deleteBusinessClassification(businessClassificationId);
        store.dispatch('setFlashMessage', { message: '業務区分情報が削除されました', type: 'success' });
        await fetchBusinessClassifications();
      } catch (error) {
        // エラー処理
        store.dispatch('setFlashMessage', { message: '業務区分情報削除に失敗しました', type: 'danger' });
      }
    };

    const handleConfirmRestore = async (businessClassificationId) => {
      try {
        await restoreBusinessClassification(businessClassificationId);
        store.dispatch('setFlashMessage', { message: '業務区分情報が復元されました', type: 'success' });
        await fetchBusinessClassifications();
      } catch (error) {
        store.dispatch('setFlashMessage', { message: '業務区分情報復元に失敗しました', type: 'danger' });
      }
    };

    // CSVインポート
    const handleFileChange = (event) => {
      selectedFile.value = event.target.files[0]; // 選択されたファイルを保存
    };

    // モーダルを閉じる処理
    const closeModal = () => {
      const modalElement = document.getElementById('exampleModal');
      const modal = Modal.getInstance(modalElement) || new Modal(modalElement);
      modal.hide();
    };

    // モーダルが閉じた後に実行される処理
    const onModalHidden = () => {
      clearErrorMessages(); // エラーメッセージをクリアする関数を呼び出す
    };

    // モーダルの閉じるボタンのアクション
    const clearErrorMessages = () => {
      errorMessages.value = {}; // エラーメッセージをクリア
      selectedFile.value = null; // ファイルをクリア

      // inputタグのvalueをリセット
      const input = document.querySelector('.form-control[type="file"]');
      if (input) {
        input.value = '';
      }
    };

    const submitFile = async () => {
      if (!selectedFile.value) {
        store.dispatch('setFlashMessage', { message: 'ファイルが選択されていません', type: 'danger' });
        return;
      }

      try {
        await importBusinessClassificationsCsv(selectedFile.value); // 選択されたファイルを関数に渡す
        store.dispatch('setFlashMessage', { message: 'アップロードに成功しました', type: 'success' });
        // モーダルを閉じる
        closeModal();

        // モーダルが完全に閉じた後に実行されるイベントリスナーを追加
        document.getElementById('exampleModal').addEventListener('hidden.bs.modal', onModalHidden, { once: true });

        // 顧客情報の再取得
        await fetchBusinessClassifications();
      } catch (error) {
        if (error.response && error.response.data) {
          // エラーメッセージをerrorMessagesにセット
          errorMessages.value = error.response.data || {};
        }
        console.error('アップロードに失敗しました', error);
        store.dispatch('setFlashMessage', { message: 'アップロードに失敗しました', type: 'danger' });
      }
    };

    const downloadCsv = async () => {
      try {
        const response = await exportBusinessClassificationsCsv({
          deleted_status: searchDeletedStatus.value
        });

        const now = new Date();
        const timestamp = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds().toString().padStart(2, '0')}`;
        const fileName = `m_business_classifications_${timestamp}.csv`;

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('CSVダウンロードエラー', error);
        store.dispatch('setFlashMessage', { message: 'CSVダウンロードに失敗しました', type: 'danger' });
      }
    };

    onMounted(fetchBusinessClassifications);

    return {
      title,
      userRole,
      business_classifications,
      paramsIdToDelete,
      paramsIdToRestore,
      handleDeleteClick,
      handleConfirmDelete,
      handleRestoreClick,
      handleConfirmRestore,
      selectedFile,
      searchDeletedStatus,
      handleFileChange,
      submitFile,
      downloadCsv,
      searchBusinessClassifications,
      searchResetBusinessClassifications,
      errorMessages,
      clearErrorMessages
    };
  }
};
</script>

<style scoped>
.master-table .btn-cell{
  width: 120px;
  min-width: 120px;
  max-width: 120px;
}

.no-active-business-classification td {
  background-color: #aaa;
}
</style>
